/**
 * Production Line Column Configuration
 * Main entry point for building tab-specific column configurations
 */

import type { OrderTableColumn } from '../../../types/components';
import type { ProductionLineStage } from '../../../utils/types';
import { BASE_PRODUCTION_COLUMNS } from './baseColumns';
import { TAB_COLUMN_CONFIGS } from './tabOverrides';

/**
 * Get the complete column configuration for a specific production line tab
 * Combines base columns with tab-specific additions, removals, and overrides
 * 
 * @param activeTab - The currently active production line tab
 * @returns Array of columns configured for the specified tab
 * 
 * @example
 * // Get columns for glass cutting tab
 * const columns = getProductionLineColumns('glass_cutting');
 * // Returns base columns + glass_cut_checkbox, print_label_glass_cutting, rack_glass_cutting
 */
export function getProductionLineColumns(
  activeTab: ProductionLineStage
): OrderTableColumn[] {
  const config = TAB_COLUMN_CONFIGS[activeTab];
  
  if (!config) {
    console.warn(`No configuration found for tab: ${activeTab}. Using base columns only.`);
    return [...BASE_PRODUCTION_COLUMNS];
  }
  
  // Step 1: Start with base columns (deep copy to avoid mutations)
  let columns = BASE_PRODUCTION_COLUMNS.map(col => ({ ...col }));
  
  // Step 2: Apply column overrides (modify existing columns)
  // This is used for things like spacer tab showing spacer dimensions instead of glass dimensions
  if (config.columnOverrides && Object.keys(config.columnOverrides).length > 0) {
    columns = columns.map(col => {
      const override = config.columnOverrides![col.key];
      if (override) {
        // Merge override properties into the column
        return { ...col, ...override };
      }
      return col;
    });
  }
  
  // Step 3: Remove hidden columns
  // This allows tabs to hide specific columns that aren't relevant
  if (config.hideColumns && config.hideColumns.length > 0) {
    columns = columns.filter(col => !config.hideColumns!.includes(col.key));
  }
  
  // Step 4: Add tab-specific columns
  // These are columns unique to this tab (workflow checkboxes, dates, etc.)
  if (config.addColumns && config.addColumns.length > 0) {
    columns = [...columns, ...config.addColumns];
  }
  
  // Step 5: Reorder columns if specified
  // This allows tabs to change the order of columns for better UX
  if (config.reorderColumns && config.reorderColumns.length > 0) {
    columns = reorderColumns(columns, config.reorderColumns);
  }
  
  return columns;
}

/**
 * Reorder columns based on a specified order array
 * Columns not in the order array are appended at the end in their original order
 * 
 * @param columns - Array of columns to reorder
 * @param order - Array of column keys in desired order
 * @returns Reordered array of columns
 * 
 * @example
 * const reordered = reorderColumns(columns, ['glass_width', 'glass_height', 'product_type']);
 * // Puts width, height, and product_type first, rest follow in original order
 */
function reorderColumns(
  columns: OrderTableColumn[],
  order: string[]
): OrderTableColumn[] {
  const columnMap = new Map(columns.map(col => [col.key, col]));
  const reordered: OrderTableColumn[] = [];
  
  // Add columns in the specified order
  for (const key of order) {
    const column = columnMap.get(key);
    if (column) {
      reordered.push(column);
      columnMap.delete(key); // Remove from map so we don't add it again
    } else {
      console.warn(`Column key "${key}" specified in reorder but not found in columns`);
    }
  }
  
  // Add remaining columns (not in order array) in their original order
  for (const column of columns) {
    if (columnMap.has(column.key)) {
      reordered.push(column);
    }
  }
  
  return reordered;
}

/**
 * Get a list of all column keys for a specific tab
 * Useful for debugging or validation
 * 
 * @param activeTab - The production line tab
 * @returns Array of column keys
 */
export function getColumnKeys(activeTab: ProductionLineStage): string[] {
  const columns = getProductionLineColumns(activeTab);
  return columns.map(col => col.key);
}

/**
 * Check if a specific column is visible on a given tab
 * 
 * @param activeTab - The production line tab
 * @param columnKey - The column key to check
 * @returns True if the column is visible on the specified tab
 */
export function isColumnVisibleOnTab(
  activeTab: ProductionLineStage,
  columnKey: string
): boolean {
  const columnKeys = getColumnKeys(activeTab);
  return columnKeys.includes(columnKey);
}

// Export everything for use in ProductionLine.tsx
export { BASE_PRODUCTION_COLUMNS } from './baseColumns';
export { TAB_COLUMN_CONFIGS } from './tabOverrides';
export type { TabColumnConfig } from './tabOverrides';
